import { useQuery } from '@tanstack/react-query';
// api
import { dashboardApi } from '@/domains/traffic-monitoring/apis/dashboard';
// constant
import {
  DASHBOARD_CONSTANTS,
  EVENT_SUMMARY_CARDS,
} from '@/domains/traffic-monitoring/constants/dashboard.constants';
import type { TrafficEventSummaryParams, TrafficEventSummaryRow } from '../types/dashboard.types';

export interface EventSummaryCard {
  title: string;
  code: string;
  total: number;
  changePct: number | null;
  typeArrow: 'up' | 'down';
}

const toCards = (events: TrafficEventSummaryRow[] | undefined): EventSummaryCard[] => {
  const byCode = new Map((events ?? []).map((e) => [e.code, e]));
  return EVENT_SUMMARY_CARDS.map(({ title, code }) => {
    const row = byCode.get(code);
    const total = row?.today ?? 0;
    const changePct = row?.change_pct ?? null;
    // up (red danger) = more incidents than yesterday; down (green) = fewer.
    const typeArrow: 'up' | 'down' = changePct === null || changePct >= 0 ? 'up' : 'down';
    return { title, total, changePct, typeArrow, code };
  });
};

export const useTrafficEventSummary = (params: TrafficEventSummaryParams = {}) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['trafficEventSummary', params],
    queryFn: () => dashboardApi.getTrafficEventSummary(params),
    staleTime: DASHBOARD_CONSTANTS.CROSSROAD_DASHBOARD.STALE_TIME_MS,
    gcTime: DASHBOARD_CONSTANTS.CROSSROAD_DASHBOARD.CACHE_TIME_MS,
    // Keep the dashboard cadence; hold the previous values while refetching.
    refetchInterval: DASHBOARD_CONSTANTS.CROSSROAD_DASHBOARD.REFETCH_INTERVAL_MS,
    placeholderData: (prev) => prev,
  });

  return {
    cards: toCards(data?.data?.events),
    totalToday: data?.data?.total?.today ?? 0,
    totalChangePct: data?.data?.total?.change_pct ?? null,
    isLoading,
    isError,
  };
};
